import { useEffect, useMemo } from 'react';
import { useGeographicFocusQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import {
  GeographicFocusRequest,
  GeographicFocusResponse,
} from 'store/api/apiTypes';

/**
 * useGeographicFocus
 * Fetches Geographic Focus rule (information completeness) analysis for selected project.
 */
export const useGeographicFocus = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const activeReport = useAppSelector(state => state.history.activeReport);
  const queryParams = useMemo<GeographicFocusRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: geographicFocus,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGeographicFocusQuery(queryParams as GeographicFocusRequest, {
    skip: !queryParams,
    refetchOnMountOrArgChange: true,
  });

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) {
      handleApiError(error);
    }
  }, [error, handleApiError]);

  usePollingOnError(queryParams, error, refetch, geographicFocus);

  const companyAnalysis = geographicFocus?.company_analysis;

  return {
    geographicFocus: geographicFocus as GeographicFocusResponse | undefined,
    companyAnalysis,
    productAnalyses: geographicFocus?.products || {},
    isLoading,
    isFetching,
    error,
    refetchGeographicFocus: refetch,
    showGeographicFocusErrorModal: showErrorModal,
    handleGeographicFocusRetry: handleRetry,
    hideGeographicFocusErrorModal: hideErrorModal,
    hasGeographicFocus: !!geographicFocus,
    companyDomain: companyAnalysis?.domain,
    finalScore: companyAnalysis?.final_score,
    overallAssessment: companyAnalysis?.overall_assessment,
  };
};
